import {SettingParam, SettingParamTypes} from "@/types/node";
import {memo, useEffect, useState, useCallback} from "react";
import {SettingVariableSelect} from "./SettingVariableSelect";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/Popover";
import {SettingInput} from "./SettingInput";
import {SettingSelect} from "./SettingSelect";
import {Button} from "@/components/ui/Button";
import {Trash} from "lucide-react";
import {Icon} from "@/components/ui/icon";
import {Plus} from "lucide-react";
import {ConditionIcon} from "@/components/icons/ConditionIcon";
import {TypeOperatorsMap, VariableType} from "./constants";
import {Condition} from "@/types/node";
import {cn} from "@/lib/utils";

const PopoverContentContainer = memo(
  ({
    value,
    selectedNodeId,
    disabled = false,
    isEditing,
    setOpen,
    handleConditionUpdate,
    handleConditionAdd,
  }: {
    value: Condition;
    selectedNodeId: string;
    disabled: boolean;
    isEditing: boolean;
    setOpen: (open: boolean) => void;
    handleConditionUpdate: (value: Condition) => void;
    handleConditionAdd: (value: Condition) => void;
  }) => {
    const [condition, setCondition] = useState<Condition>(value);

    useEffect(() => {
      setCondition(value);
    }, [value, selectedNodeId]);

    const isInteger = (v: string) => /^-?\d+$/.test(v.trim());
    const isNumber = (v: string) => /^-?\d+(\.\d+)?$/.test(v.trim());
    const isBoolean = (v: string) => /^(true|false)$/.test(v.trim());

    let valueError: string | undefined;
    const variableType = condition?.variable?.type;
    const valueStr = condition?.value?.toString() || "";

    if (variableType && valueStr.length > 0) {
      switch (variableType) {
        case VariableType.INTEGER:
          if (!isInteger(valueStr)) valueError = "Enter a valid integer";
          break;
        case VariableType.NUMBER:
          if (!isNumber(valueStr)) valueError = "Enter a valid number";
          break;
        case VariableType.BOOLEAN:
          if (!isBoolean(valueStr)) valueError = "Enter true or false";
          break;
        case VariableType.STRING:
        default:
          // Non-empty already enforced elsewhere; no extra validation
          break;
      }
    }

    const isFormInvalid =
      !condition?.variable ||
      !condition?.operator ||
      !condition?.value ||
      !!valueError;

    return (
      <PopoverContent side="left" sideOffset={8} className="w-98">
        <div className="flex flex-col">
          <div className="flex flex-row gap-4">
            <div className="flex-grow min-w-0">
              <SettingVariableSelect
                param={{
                  label: "When Variable",
                  type: SettingParamTypes.VARIABLE_SELECT,
                  key: "variable",
                  value: condition?.variable?.name,
                }}
                handleChange={(_param, value) => {
                  setCondition({...condition, variable: value});
                }}
                selectedNodeId={selectedNodeId}
                disabled={disabled}
                placeholder="Select Variable"
                tooltipMessage="Variables can be collected in Instruction nodes placed before this branch."
              />
            </div>
            <div className="w-40 shrink-0">
              <SettingSelect
                param={{
                  label: "Operator",
                  type: SettingParamTypes.SELECT,
                  key: "type",
                  value: condition?.operator,
                }}
                handleChange={(_param, value) => {
                  setCondition({...condition, operator: value});
                  setOpen(true);
                }}
                placeholder="Select Operator"
                disabled={disabled}
                selectOptions={TypeOperatorsMap[condition?.variable?.type]}
                selectedNodeId={selectedNodeId}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <SettingInput
              param={{
                label: "Target Value",
                type: SettingParamTypes.INPUT,
                placeholder: "Enter value",
                key: "value",
                value: condition?.value,
              }}
              handleChange={(_param, value) => {
                const variableType = condition?.variable?.type;
                const normalizedValue =
                  variableType === VariableType.BOOLEAN
                    ? String(value ?? "")
                        .trim()
                        .toLowerCase()
                    : value;
                setCondition({...condition, value: normalizedValue});
              }}
              selectedNodeId={selectedNodeId}
              disabled={disabled}
              error={valueError}
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                if (isEditing) {
                  handleConditionUpdate(condition);
                } else {
                  handleConditionAdd(condition);
                }
              }}
              disabled={isFormInvalid || disabled}
            >
              {isEditing ? "Update Rule" : "Add Rule"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    );
  },
);
PopoverContentContainer.displayName = "PopoverContentContainer";

const ConditionItem = memo(
  ({
    condition,
    index,
    currentIndex,
    open,
    disabled,
    selectedNodeId,
    currentCondition,
    isEditing,
    setOpen,
    setPopoverTopPosition,
    onConditionClick,
    onConditionDelete,
    handleConditionUpdate,
    handleConditionAdd,
  }: {
    condition: Condition;
    index: number;
    currentIndex: number | undefined;
    open: boolean;
    disabled: boolean;
    selectedNodeId: string;
    currentCondition: Condition;
    isEditing: boolean;
    setOpen: (open: boolean) => void;
    setPopoverTopPosition: (pos: number) => void;
    onConditionClick: (
      condition: Condition | null,
      index: number | undefined,
    ) => void;
    onConditionDelete: (condition: Condition) => void;
    handleConditionUpdate: (value: Condition) => void;
    handleConditionAdd: (value: Condition) => void;
  }) => {
    const operatorOptions =
      TypeOperatorsMap[condition.variable?.type as VariableType];
    const operatorName = operatorOptions?.find(
      op => op.value === condition.operator,
    )?.name;

    return (
      <Popover
        key={index}
        open={open && currentIndex === index}
        setOpen={isOpen => {
          setOpen(isOpen);
          if (!isOpen) onConditionClick(null, undefined);
        }}
      >
        <PopoverTrigger className="w-full">
          <div
            ref={ref => {
              if (ref) setPopoverTopPosition(ref.offsetTop);
            }}
            onClick={() => {
              onConditionClick(condition, index);
            }}
            className="border rounded-lg p-2.5 flex flex-row justify-between items-center gap-2.5 cursor-pointer"
          >
            <div>
              <Icon className="w-11 h-11 bg-slate">
                <ConditionIcon className="w-6 h-6 text-secondary" />
              </Icon>
            </div>
            <div className="flex flex-col overflow-hidden whitespace-nowrap items-start">
              <p className="text-sm text-primary font-medium overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
                {condition.variable?.name}
              </p>
              <div className="flex flex-row gap-1 text-secondary font-normal text-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
                {condition.operator && (
                  <div>{operatorName || condition.operator}</div>
                )}
                {condition.value && (
                  <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5 overflow-hidden whitespace-nowrap text-ellipsis">
                    {condition.value}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              className="[&_svg]:size-5 ml-auto"
              disabled={disabled}
              asDiv
              onClick={e => {
                e.stopPropagation();
                onConditionDelete(condition);
              }}
            >
              <Trash className="text-secondary" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContentContainer
          value={currentCondition}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
          setOpen={setOpen}
          isEditing={isEditing}
          handleConditionUpdate={handleConditionUpdate}
          handleConditionAdd={handleConditionAdd}
        />
      </Popover>
    );
  },
);
ConditionItem.displayName = "ConditionItem";

export const SettingDeterministicCondition = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam & {value: Condition[]};
  handleChange: (param: SettingParam, value: Condition[]) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCondition, setCurrentCondition] = useState<Condition>();
  const [currentIndex, setCurrentIndex] = useState<number>();
  const [values, setValues] = useState<Condition[]>(param.value || []);
  const [, setPopoverTopPosition] = useState<number>(0);

  useEffect(() => {
    setValues(param.value || []);
  }, [param, selectedNodeId]);

  const handleConditionAdd = useCallback(
    (value: Condition) => {
      setValues(prev => {
        const newValues = [...prev, value];
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [param, handleChange],
  );

  const handleConditionUpdate = useCallback(
    (value: Condition) => {
      setValues(prev => {
        const newValues = prev.map((v, i) => (i === currentIndex ? value : v));
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [currentIndex, param, handleChange],
  );

  const handleConditionDelete = useCallback(
    (index: number) => {
      setValues(prev => {
        const newValues = prev.filter((_, i) => i !== index);
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [param, handleChange],
  );

  const handleConditionClick = useCallback(
    (variable: Condition | null, index: number | undefined) => {
      if (variable) {
        setCurrentCondition({...variable});
        setCurrentIndex(index);
        setIsEditing(true);
        setOpen(true);
      } else {
        setCurrentIndex(undefined);
      }
    },
    [],
  );

  return (
    <div>
      <Popover
        open={open && currentIndex === undefined}
        setOpen={isOpen => {
          setOpen(isOpen);
          if (!isOpen) setCurrentIndex(undefined);
        }}
      >
        <PopoverTrigger
          className={cn("w-full", disabled && "opacity-50 pointer-events-none")}
        >
          <Button
            variant="outline"
            className="w-full px-3 gap-1.5"
            asDiv
            onClick={() => {
              setIsEditing(false);
              setCurrentCondition({
                variable: null,
                operator: "",
                value: "",
              });
              setCurrentIndex(undefined);
            }}
          >
            <Plus className="text-primary-accent w-4 h-4" />
            Add Rule
          </Button>
        </PopoverTrigger>
        <PopoverContentContainer
          value={currentCondition}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
          setOpen={setOpen}
          isEditing={isEditing}
          handleConditionUpdate={handleConditionUpdate}
          handleConditionAdd={handleConditionAdd}
        />
      </Popover>
      <div className="flex flex-col gap-3 mt-4">
        {[...values].map((condition, index) => (
          <ConditionItem
            key={index}
            condition={condition}
            index={index}
            currentIndex={currentIndex}
            open={open}
            setOpen={setOpen}
            setPopoverTopPosition={setPopoverTopPosition}
            onConditionClick={handleConditionClick}
            onConditionDelete={() => handleConditionDelete(index)}
            disabled={disabled}
            selectedNodeId={selectedNodeId}
            currentCondition={currentCondition}
            handleConditionUpdate={handleConditionUpdate}
            handleConditionAdd={handleConditionAdd}
            isEditing={isEditing}
          />
        ))}
      </div>
    </div>
  );
};
